/* eslint-disable @typescript-eslint/no-explicit-any */
import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import {
  GalleryConfig,
  GalleryEvent,
  GalleryPhoto,
  CustomerUser,
  CustomerOrder,
  GalleryCart,
  GalleryApiResponse,
  GalleryEventsResponse,
  GalleryPhotosResponse,
  CustomerOrdersResponse,
  EventFilters,
  PhotoFilters,
} from '../types/gallery';

class GalleryApiService {
  private api: AxiosInstance;
  private baseURL: string;
  private eventPhotosCache = new Map<string, GalleryPhoto[]>();

  constructor() {
    this.baseURL = import.meta.env.VITE_API_URL || 'https://api.pixel-share.com/v1';

    this.api = axios.create({
      baseURL: this.baseURL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add auth token
    this.api.interceptors.request.use(
      config => {
        // Get photographer ID from domain info
        const domainInfo = this.getDomainInfo();
        if (domainInfo?.photographerId) {
          // Add photographer context to headers
          config.headers['X-Photographer-ID'] = domainInfo.photographerId;

          // Add customer auth token if available
          const token = this.getAuthToken(domainInfo.photographerId);
          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
          }
        }

        return config;
      },
      error => Promise.reject(error)
    );

    // Response interceptor for error handling
    this.api.interceptors.response.use(
      response => response,
      error => {
        if (error.response?.status === 401) {
          // Clear invalid token and redirect to login
          const domainInfo = this.getDomainInfo();
          if (domainInfo?.photographerId) {
            this.clearAuthToken(domainInfo.photographerId);
          }
        }
        return Promise.reject(error);
      }
    );
  }

  private getDomainInfo() {
    try {
      const domainInfo = sessionStorage.getItem('gallery_domain_info');
      return domainInfo ? JSON.parse(domainInfo) : null;
    } catch {
      return null;
    }
  }

  private getAuthToken(photographerId: string): string | null {
    return (
      localStorage.getItem(`gallery_token_${photographerId}`) ||
      sessionStorage.getItem(`gallery_token_${photographerId}`)
    );
  }

  private clearAuthToken(photographerId: string): void {
    localStorage.removeItem(`gallery_token_${photographerId}`);
    sessionStorage.removeItem(`gallery_token_${photographerId}`);
  }

  private unwrapResponse<T>(responseData: GalleryApiResponse<T> | T): T {
    if (
      responseData &&
      typeof responseData === 'object' &&
      'success' in responseData &&
      'data' in responseData
    ) {
      return (responseData as GalleryApiResponse<T>).data;
    }

    return responseData as T;
  }

  private buildDefaultSettings(): GalleryConfig['settings'] {
    return {
      allowGuestPurchases: true,
      requireRegistration: false,
      requireEmailVerification: false,
      showPrices: true,
      showPhotographer: true,
      enableSocialSharing: true,
      enableFavorites: true,
      enableComments: false,
      autoApproveComments: false,
      downloadQuality: 'web',
      maxCartItems: 100,
      currency: 'USD',
      timezone: 'America/New_York',
      locale: 'en-US',
      analytics: {
        enableTracking: false,
      },
    };
  }

  private normalizeConfig(rawConfig: any, fallbackSlugOrDomain: string): GalleryConfig {
    const slug = rawConfig.photographer_slug || rawConfig.subdomain || fallbackSlugOrDomain;
    const businessName =
      rawConfig.business_name ||
      rawConfig.businessName ||
      rawConfig.photographerName ||
      rawConfig.branding?.businessName ||
      slug;
    const branding = rawConfig.branding || {};
    const domain = rawConfig.custom_domain || rawConfig.customDomain || rawConfig.domain || `${slug}.pixel-share.com`;

    return {
      photographerId: slug,
      businessName,
      domain,
      subdomain: slug,
      customDomain: rawConfig.custom_domain || rawConfig.customDomain,
      branding: {
        logo: branding.logo_url || branding.logo,
        favicon: branding.favicon_url || branding.favicon,
        primaryColor: branding.primary_color || branding.primaryColor || '#3B82F6',
        secondaryColor: branding.secondary_color || branding.secondaryColor || '#ffffff',
        fonts: {
          primary: branding.font_family || branding.fonts?.primary || 'Inter',
        },
        layout: {
          type: branding.gallery_layout || branding.layout?.type || 'grid',
          columns: branding.layout?.columns || 4,
          spacing: branding.layout?.spacing || 'normal',
          borderRadius: branding.layout?.borderRadius || 'medium',
        },
        customCss: branding.custom_css || branding.customCss,
        showWatermark: Boolean(branding.showWatermark),
        watermarkPosition: branding.watermarkPosition || 'bottom-right',
      },
      settings: {
        ...this.buildDefaultSettings(),
        ...rawConfig.settings,
      },
      seo: {
        title: rawConfig.seo?.title || businessName,
        description:
          rawConfig.seo?.description ||
          rawConfig.portfolio_description ||
          `Photo galleries by ${businessName}`,
        keywords: rawConfig.seo?.keywords || [],
        ogImage: rawConfig.seo?.ogImage,
        structuredData: {
          enabled: true,
          organizationType: 'ProfessionalService',
          ...rawConfig.seo?.structuredData,
        },
      },
    };
  }

  private normalizeEvent(rawEvent: any, photographerId: string): GalleryEvent {
    const eventId = rawEvent.id || rawEvent.event_id || rawEvent.eventId;
    const totalPhotos = rawEvent.totalPhotos ?? rawEvent.photo_count ?? rawEvent.photoCount ?? 0;

    return {
      id: eventId,
      photographerId,
      title: rawEvent.title || rawEvent.event_name || rawEvent.eventName || 'Untitled Gallery',
      description: rawEvent.description,
      eventDate: rawEvent.eventDate || rawEvent.event_date || rawEvent.eventDateTime || '',
      location: rawEvent.location,
      coverImage: rawEvent.coverImage || rawEvent.thumbnail_url || rawEvent.thumbnailUrl,
      status: rawEvent.status || 'published',
      settings: {
        allowDownloads: rawEvent.gallery_settings?.allow_downloads ?? false,
        showExifData: rawEvent.gallery_settings?.show_exif_data ?? false,
        enableSocialSharing: rawEvent.gallery_settings?.enable_social_sharing ?? true,
        requireLogin: rawEvent.gallery_settings?.require_login ?? false,
      },
      pricing: {
        web: rawEvent.pricing?.web ?? rawEvent.pricing?.digital_download_price ?? 5,
        print: rawEvent.pricing?.print ?? rawEvent.pricing?.print_price ?? 15,
        full: rawEvent.pricing?.full ?? rawEvent.pricing?.full_resolution_price ?? 25,
      },
      seo: {
        title: rawEvent.seo?.title || rawEvent.seo_data?.title,
        description: rawEvent.seo?.description || rawEvent.seo_data?.description,
        keywords: rawEvent.seo?.keywords || rawEvent.seo_data?.keywords,
        ogImage: rawEvent.seo?.ogImage || rawEvent.seo_data?.og_image,
      },
      statistics: {
        views: rawEvent.statistics?.views ?? 0,
        uniqueVisitors: rawEvent.statistics?.uniqueVisitors ?? 0,
        favorites: rawEvent.statistics?.favorites ?? 0,
        shares: rawEvent.statistics?.shares ?? 0,
        downloads: rawEvent.statistics?.downloads ?? 0,
      },
      totalPhotos,
      featuredPhotos: rawEvent.featuredPhotos || [],
      createdAt: rawEvent.createdAt || rawEvent.created_at || rawEvent.event_date || '',
      updatedAt: rawEvent.updatedAt || rawEvent.updated_at || rawEvent.event_date || '',
    };
  }

  private normalizePhoto(rawPhoto: any, photographerId: string, eventId: string): GalleryPhoto {
    const photoId = rawPhoto.id || rawPhoto.media_id || rawPhoto.mediaId;
    const urls = rawPhoto.urls || {};
    const webUrl = urls.web || urls.watermarked || urls.thumbnail || '';

    return {
      id: photoId,
      eventId: rawPhoto.eventId || rawPhoto.event_id || eventId,
      photographerId,
      title: rawPhoto.title || rawPhoto.filename,
      description: rawPhoto.description,
      filename: rawPhoto.filename || photoId,
      originalKey: rawPhoto.originalKey || '',
      webKey: rawPhoto.webKey || '',
      thumbnailKey: rawPhoto.thumbnailKey || '',
      metadata: {
        width: rawPhoto.metadata?.width || rawPhoto.width || 0,
        height: rawPhoto.metadata?.height || rawPhoto.height || 0,
        fileSize: rawPhoto.metadata?.fileSize || 0,
        format: rawPhoto.metadata?.format || 'jpg',
      },
      tags: rawPhoto.tags || [],
      isFeatured: rawPhoto.isFeatured ?? rawPhoto.is_featured ?? false,
      isWatermarked: Boolean(urls.watermarked),
      downloadCount: rawPhoto.downloadCount || 0,
      favoriteCount: rawPhoto.favoriteCount || 0,
      status: rawPhoto.status || 'ready',
      pricing: {
        web: rawPhoto.pricing?.web ?? 5,
        print: rawPhoto.pricing?.print ?? 15,
        full: rawPhoto.pricing?.full ?? 25,
      },
      urls: {
        thumbnail: urls.thumbnail || webUrl,
        web: webUrl,
        watermarked: urls.watermarked,
        print: urls.print,
        full: urls.full,
      },
      createdAt: rawPhoto.createdAt || rawPhoto.uploaded_at || '',
      updatedAt: rawPhoto.updatedAt || rawPhoto.uploaded_at || '',
    };
  }

  // Gallery Configuration
  async getGalleryConfig(domainOrSlug: string): Promise<GalleryConfig> {
    const domainInfo = this.getDomainInfo();
    const slug = domainInfo?.subdomain || domainInfo?.photographerId;

    if (slug) {
      const response = await this.api.get(`/gallery/${slug}/config`);
      return this.normalizeConfig(this.unwrapResponse(response.data), slug);
    }

    const response = await this.api.get(`/gallery/config?domain=${encodeURIComponent(domainOrSlug)}`);
    return this.normalizeConfig(this.unwrapResponse(response.data), domainOrSlug);
  }

  // Events
  async getEvents(photographerId: string, filters?: EventFilters): Promise<GalleryEventsResponse> {
    const params = new URLSearchParams();
    if (filters?.search) params.append('search', filters.search);
    if (filters?.dateRange) {
      params.append('start_date', filters.dateRange.start);
      params.append('end_date', filters.dateRange.end);
    }
    if (filters?.location) params.append('location', filters.location);
    if (filters?.status) params.append('status', filters.status);
    if (filters?.tags) params.append('tags', filters.tags.join(','));
    if (filters?.sortBy) params.append('sort_by', filters.sortBy);
    if (filters?.sortOrder) params.append('sort_order', filters.sortOrder);

    const response = await this.api.get(
      `/gallery/${photographerId}/events?${params}`
    );
    const rawData = this.unwrapResponse<any>(response.data);
    const rawEvents = rawData.items || rawData.events || [];
    const events = rawEvents.map((event: any) => this.normalizeEvent(event, photographerId));
    const page = rawData.pagination?.page ?? 1;
    const limit = rawData.pagination?.limit ?? events.length;
    const total = rawData.pagination?.total ?? events.length;

    return {
      items: events,
      pagination: {
        page,
        limit,
        total,
        totalPages: limit ? Math.ceil(total / limit) : 1,
        hasNext: rawData.pagination?.hasNext ?? rawData.pagination?.has_more ?? false,
        hasPrevious: page > 1,
      },
    };
  }

  async getEvent(
    photographerId: string,
    eventId: string,
    password?: string
  ): Promise<GalleryEvent> {
    const config: AxiosRequestConfig = {};
    if (password) {
      config.headers = { 'X-Gallery-Password': password };
    }

    const response = await this.api.get(
      `/gallery/${photographerId}/events/${eventId}`,
      config
    );
    const rawEvent = this.unwrapResponse<any>(response.data);
    const event = this.normalizeEvent(rawEvent, photographerId);
    const photos = (rawEvent.photos || []).map((photo: any) =>
      this.normalizePhoto(photo, photographerId, event.id)
    );

    this.eventPhotosCache.set(`${photographerId}:${event.id}`, photos);
    return event;
  }

  // Photos
  async getEventPhotos(
    photographerId: string,
    eventId: string,
    filters?: PhotoFilters & { page?: number; limit?: number }
  ): Promise<GalleryPhotosResponse> {
    let photos = this.eventPhotosCache.get(`${photographerId}:${eventId}`);
    if (!photos) {
      await this.getEvent(photographerId, eventId);
      photos = this.eventPhotosCache.get(`${photographerId}:${eventId}`) || [];
    }

    if (filters?.search) {
      const search = filters.search.toLowerCase();
      photos = photos.filter(photo =>
        [photo.title, photo.filename, photo.description].some(value =>
          value?.toLowerCase().includes(search)
        )
      );
    }

    if (filters?.tags?.length) {
      photos = photos.filter(photo => filters.tags?.some(tag => photo.tags.includes(tag)));
    }

    if (filters?.isFeatured !== undefined) {
      photos = photos.filter(photo => photo.isFeatured === filters.isFeatured);
    }

    const page = filters?.page || 1;
    const limit = filters?.limit || photos.length || 24;
    const start = (page - 1) * limit;
    const pagedPhotos = photos.slice(start, start + limit);

    return {
      items: pagedPhotos,
      pagination: {
        page,
        limit,
        total: photos.length,
        totalPages: Math.ceil(photos.length / limit) || 1,
        hasNext: start + limit < photos.length,
        hasPrevious: page > 1,
      },
    };
  }

  async getPhoto(photographerId: string, photoId: string): Promise<GalleryPhoto> {
    const response = await this.api.get<GalleryApiResponse<GalleryPhoto>>(
      `/gallery/${photographerId}/photos/${photoId}`
    );
    return response.data.data;
  }

  async getPhotoDownloadUrl(
    photographerId: string,
    photoId: string,
    resolution: 'web' | 'print' | 'full'
  ): Promise<{ url: string; expiresAt: string }> {
    const response = await this.api.post<GalleryApiResponse<{ url: string; expiresAt: string }>>(
      `/gallery/${photographerId}/photos/${photoId}/download`,
      { resolution }
    );
    return response.data.data;
  }

  // Customer Authentication
  async customerLogin(
    photographerId: string,
    credentials: { email: string; password: string; remember?: boolean }
  ): Promise<{ customer: CustomerUser; token: string }> {
    const response = await this.api.post<
      GalleryApiResponse<{ customer: CustomerUser; token: string }>
    >(`/gallery/${photographerId}/auth/login`, credentials);
    return response.data.data;
  }

  async customerRegister(
    photographerId: string,
    userData: {
      email: string;
      password: string;
      name: string;
      phone?: string;
    }
  ): Promise<{ customer: CustomerUser; token: string }> {
    const response = await this.api.post<
      GalleryApiResponse<{ customer: CustomerUser; token: string }>
    >(`/gallery/${photographerId}/auth/register`, userData);
    return response.data.data;
  }

  async customerLogout(photographerId: string): Promise<void> {
    await this.api.post(`/gallery/${photographerId}/auth/logout`);
  }

  async getCurrentCustomer(photographerId: string): Promise<CustomerUser> {
    const response = await this.api.get<GalleryApiResponse<CustomerUser>>(
      `/gallery/${photographerId}/auth/me`
    );
    return response.data.data;
  }

  async updateCustomerProfile(
    photographerId: string,
    customerId: string,
    updates: Partial<CustomerUser>
  ): Promise<CustomerUser> {
    const response = await this.api.patch<GalleryApiResponse<CustomerUser>>(
      `/gallery/${photographerId}/customers/${customerId}/profile`,
      updates
    );
    return response.data.data;
  }

  async updateCustomerPreferences(
    photographerId: string,
    customerId: string,
    preferences: Partial<CustomerUser['preferences']>
  ): Promise<CustomerUser> {
    const response = await this.api.patch<GalleryApiResponse<CustomerUser>>(
      `/gallery/${photographerId}/customers/${customerId}/preferences`,
      preferences
    );
    return response.data.data;
  }

  async requestPasswordReset(photographerId: string, email: string): Promise<void> {
    await this.api.post(`/gallery/${photographerId}/auth/reset-password`, { email });
  }

  async confirmPasswordReset(
    photographerId: string,
    token: string,
    newPassword: string
  ): Promise<void> {
    await this.api.post(`/gallery/${photographerId}/auth/reset-password/confirm`, {
      token,
      password: newPassword,
    });
  }

  async resendVerificationEmail(photographerId: string, email: string): Promise<void> {
    await this.api.post(`/gallery/${photographerId}/auth/verify/resend`, { email });
  }

  async verifyEmail(photographerId: string, token: string): Promise<CustomerUser> {
    const response = await this.api.post<GalleryApiResponse<CustomerUser>>(
      `/gallery/${photographerId}/auth/verify`,
      { token }
    );
    return response.data.data;
  }

  // Cart Management
  async getCustomerCart(photographerId: string, customerId: string): Promise<GalleryCart> {
    const response = await this.api.get<GalleryApiResponse<GalleryCart>>(
      `/gallery/${photographerId}/customers/${customerId}/cart`
    );
    return response.data.data;
  }

  async saveCustomerCart(
    photographerId: string,
    customerId: string,
    cart: GalleryCart
  ): Promise<void> {
    await this.api.put(`/gallery/${photographerId}/customers/${customerId}/cart`, cart);
  }

  async validateCart(
    photographerId: string,
    cart: GalleryCart
  ): Promise<{ valid: boolean; errors: string[] }> {
    const response = await this.api.post<GalleryApiResponse<{ valid: boolean; errors: string[] }>>(
      `/gallery/${photographerId}/cart/validate`,
      cart
    );
    return response.data.data;
  }

  async validateCoupon(
    photographerId: string,
    couponCode: string,
    cart: GalleryCart
  ): Promise<{ discount: number; type: 'percentage' | 'fixed' }> {
    const response = await this.api.post<
      GalleryApiResponse<{ discount: number; type: 'percentage' | 'fixed' }>
    >(`/gallery/${photographerId}/coupons/validate`, { code: couponCode, cart });
    return response.data.data;
  }

  // Orders
  async getCustomerOrders(
    photographerId: string,
    customerId: string,
    page = 1,
    limit = 20
  ): Promise<CustomerOrdersResponse> {
    const response = await this.api.get<GalleryApiResponse<CustomerOrdersResponse>>(
      `/gallery/${photographerId}/customers/${customerId}/orders?page=${page}&limit=${limit}`
    );
    return response.data.data;
  }

  async getOrder(photographerId: string, orderId: string): Promise<CustomerOrder> {
    const response = await this.api.get<GalleryApiResponse<CustomerOrder>>(
      `/gallery/${photographerId}/orders/${orderId}`
    );
    return response.data.data;
  }

  async createOrder(
    photographerId: string,
    orderData: {
      customerId?: string;
      cart: GalleryCart;
      customerInfo: {
        name: string;
        email: string;
        phone?: string;
      };
    }
  ): Promise<{ order: CustomerOrder; paymentIntent: { clientSecret: string } }> {
    const response = await this.api.post<
      GalleryApiResponse<{ order: CustomerOrder; paymentIntent: { clientSecret: string } }>
    >(`/gallery/${photographerId}/orders`, orderData);
    return response.data.data;
  }

  async confirmOrderPayment(
    photographerId: string,
    orderId: string,
    paymentIntentId: string
  ): Promise<CustomerOrder> {
    const response = await this.api.post<GalleryApiResponse<CustomerOrder>>(
      `/gallery/${photographerId}/orders/${orderId}/confirm`,
      { paymentIntentId }
    );
    return response.data.data;
  }

  // Favorites
  async addToFavorites(photographerId: string, customerId: string, photoId: string): Promise<void> {
    await this.api.post(`/gallery/${photographerId}/customers/${customerId}/favorites`, {
      photoId,
    });
  }

  async removeFromFavorites(
    photographerId: string,
    customerId: string,
    photoId: string
  ): Promise<void> {
    await this.api.delete(
      `/gallery/${photographerId}/customers/${customerId}/favorites/${photoId}`
    );
  }

  async getFavorites(photographerId: string, customerId: string): Promise<GalleryPhoto[]> {
    const response = await this.api.get<GalleryApiResponse<GalleryPhoto[]>>(
      `/gallery/${photographerId}/customers/${customerId}/favorites`
    );
    return response.data.data;
  }

  // Analytics (for tracking user behavior)
  async trackEvent(
    _photographerId: string,
    _event: {
      type: 'page_view' | 'photo_view' | 'cart_add' | 'order_start' | 'order_complete';
      eventId?: string;
      photoId?: string;
      orderId?: string;
      metadata?: Record<string, any>;
    }
  ): Promise<void> {
    // The public Gallery Lambda does not expose analytics ingestion yet.
    return Promise.resolve();
  }
}

export const galleryApiService = new GalleryApiService();
